import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import { Plus, Edit, Trash2 } from "lucide-react";
import { useSearchMcpTools } from "../api/useSearchMcpTools";
import { useDeleteMcpTool } from "../api/useDeleteMcpTool";
import { useEnableMcpTool } from "../api/useEnableMcpTool";
import { useDisableMcpTool } from "../api/useDisableMcpTool";
import { McpTool } from "../models/McpTool";
import { McpToolFormModal } from "./McpToolFormModal";

interface McpServersModalProps {
  open: boolean;
  onClose: () => void;
}

export function McpServersModal({ open, onClose }: McpServersModalProps) {
  const { mcpTools, searchMcpTools, isLoading, setMcpTools } =
    useSearchMcpTools();
  const { deleteMcpTool, isDeleting } = useDeleteMcpTool();
  const { enableMcpTool, isEnabling } = useEnableMcpTool();
  const { disableMcpTool, isDisabling } = useDisableMcpTool();

  const [showFormModal, setShowFormModal] = useState(false);
  const [editingTool, setEditingTool] = useState<McpTool | undefined>();

  useEffect(() => {
    if (open) {
      searchMcpTools();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleDelete = async (toolName: string) => {
    if (
      confirm(`Are you sure you want to delete the MCP tool "${toolName}"?`)
    ) {
      await deleteMcpTool(toolName);
      searchMcpTools(); // Refresh the list
    }
  };

  const handleToggleEnabled = async (tool: McpTool) => {
    try {
      if (tool.isEnabled) {
        await disableMcpTool(tool.name);
      } else {
        await enableMcpTool(tool.name);
      }

      // Update the local state directly instead of refreshing
      if (mcpTools) {
        const updatedTools = mcpTools.map((t) =>
          t.name === tool.name ? { ...t, isEnabled: !t.isEnabled } : t,
        );
        setMcpTools(updatedTools);
      }
    } catch (error) {
      console.error("Error toggling MCP tool:", error);
      // On error, refresh the list to ensure consistency
      searchMcpTools();
    }
  };

  const handleAddNew = () => {
    setEditingTool(undefined);
    setShowFormModal(true);
  };

  const handleEdit = (tool: McpTool) => {
    setEditingTool(tool);
    setShowFormModal(true);
  };

  const handleFormSuccess = () => {
    searchMcpTools(); // Refresh the list
  };

  const handleFormClose = () => {
    setShowFormModal(false);
    setEditingTool(undefined);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Configure MCP Servers</DialogTitle>
        </DialogHeader>
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-medium">MCP Tools</h3>
            <Button onClick={handleAddNew} className="flex items-center gap-2">
              <Plus className="h-4 w-4" />
              Add New Tool
            </Button>
          </div>

          {isLoading ? (
            <div className="text-center py-8">Loading MCP tools...</div>
          ) : (
            <div className="overflow-auto max-h-96">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>URL</TableHead>
                    <TableHead>Enabled</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mcpTools && mcpTools.length > 0 ? (
                    mcpTools.map((tool) => (
                      <TableRow key={tool.name}>
                        <TableCell className="font-medium">
                          {tool.name}
                        </TableCell>
                        <TableCell className="max-w-xs truncate">
                          {tool.url}
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={tool.isEnabled}
                            onCheckedChange={() => handleToggleEnabled(tool)}
                            disabled={isEnabling || isDisabling}
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEdit(tool)}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(tool.name)}
                              disabled={isDeleting}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={4} className="text-center py-8">
                        No MCP tools found. Click "Add New Tool" to create one.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </DialogContent>

      <McpToolFormModal
        open={showFormModal}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
        tool={editingTool}
      />
    </Dialog>
  );
}
